import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
    CardContent,
    CardHeader,
    CardTitle,
    CardDescription,
} from '@/src/shadComponents/ui/card';
import { Button } from '@/src/shadComponents/ui/button';
import { Input } from '@/src/shadComponents/ui/input';
import { Switch } from '@/src/shadComponents/ui/switch';
import { Label } from '@/src/shadComponents/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/src/shadComponents/ui/select';
import useWebhookSettings, {
    AuthType,
    WebhookSettings as WebhookSettingsType,
} from './hooks/useWebhookSettings';
import Loading from '@/src/Common/components/Loading';

export default function WebhookSettings() {
    const { activationId: id } = useParams();
    const activationId = id ? +id : 0;

    const {
        getWebhookSettings,
        createWebhookSettings,
        updateWebhookSettings,
        deleteWebhookSettings,
        testWebhook,
        isLoading,
    } = useWebhookSettings();

    const [webhookSettings, setWebhookSettings] =
        useState<WebhookSettingsType | null>(null);
    const [webhookUrl, setWebhookUrl] = useState<string>('');
    const [enabled, setEnabled] = useState<boolean>(true);
    const [authType, setAuthType] = useState<AuthType>('none');
    const [authValue, setAuthValue] = useState<string>('');
    const [customHeaderName, setCustomHeaderName] = useState<string>('');

    const [isSaving, setIsSaving] = useState<boolean>(false);
    const [isDeleting, setIsDeleting] = useState<boolean>(false);
    const [isTesting, setIsTesting] = useState<boolean>(false);
    const [saveError, setSaveError] = useState<string | null>(null);
    const [saveSuccess, setSaveSuccess] = useState<boolean>(false);
    const [testResult, setTestResult] = useState<{
        success: boolean;
        message: string;
    } | null>(null);
    const [hasChanges, setHasChanges] = useState<boolean>(false);
    const [permissionError, setPermissionError] = useState<boolean>(false);

    // Load existing webhook settings on component mount
    useEffect(() => {
        const loadWebhookSettings = async () => {
            try {
                const settings = await getWebhookSettings(activationId);
                setWebhookSettings(settings);
                if (settings) {
                    setWebhookUrl(settings.webhookUrl || '');
                    setEnabled(settings.enabled ?? true);
                    setAuthType(settings.authType || 'none');
                    setCustomHeaderName(settings.customHeaderName || '');
                    setAuthValue(''); // Never pre-populate for security
                }
            } catch (err: any) {
                console.error('Error loading webhook settings:', err);
                if (err.response?.status === 401) {
                    setPermissionError(true);
                }
            }
        };

        if (activationId) {
            loadWebhookSettings();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activationId]);

    // Track changes
    useEffect(() => {
        const originalUrl = webhookSettings?.webhookUrl || '';
        const originalEnabled = webhookSettings?.enabled ?? true;
        const originalAuthType = webhookSettings?.authType || 'none';
        const originalHeaderName = webhookSettings?.customHeaderName || '';

        const hasUrlChanges = webhookUrl !== originalUrl;
        const hasEnabledChanges = enabled !== originalEnabled;
        const hasAuthTypeChanges = authType !== originalAuthType;
        const hasHeaderNameChanges = customHeaderName !== originalHeaderName;
        const hasAuthValue = authValue.length > 0;

        setHasChanges(
            hasUrlChanges ||
                hasEnabledChanges ||
                hasAuthTypeChanges ||
                hasHeaderNameChanges ||
                hasAuthValue,
        );
    }, [
        webhookUrl,
        enabled,
        authType,
        authValue,
        customHeaderName,
        webhookSettings,
    ]);

    const handleSave = async () => {
        if (!webhookUrl.trim()) {
            setSaveError('Webhook URL is required');
            return;
        }

        // Validate URL
        try {
            new URL(webhookUrl);
        } catch {
            setSaveError('Invalid webhook URL format');
            return;
        }

        setIsSaving(true);
        setSaveError(null);
        setSaveSuccess(false);

        try {
            let result;
            const payload = {
                webhookUrl,
                enabled,
                authType,
                customHeaderName:
                    authType === 'custom_header' ? customHeaderName : undefined,
                authValue: authValue || undefined,
            };

            if (webhookSettings) {
                result = await updateWebhookSettings(activationId, payload);
            } else {
                result = await createWebhookSettings(activationId, payload);
            }

            if (result) {
                setWebhookSettings(result);
                setSaveSuccess(true);
                setHasChanges(false);
                setAuthValue(''); // Clear for security
                setTimeout(() => setSaveSuccess(false), 3000);
            } else {
                setSaveError('Failed to save webhook settings');
            }
        } catch (err) {
            setSaveError('An unexpected error occurred');
        } finally {
            setIsSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!webhookSettings) return;

        const confirmed = window.confirm(
            'Are you sure you want to delete the webhook configuration?',
        );
        if (!confirmed) return;

        setIsDeleting(true);
        setSaveError(null);

        try {
            const success = await deleteWebhookSettings(activationId);
            if (success) {
                setWebhookSettings(null);
                setWebhookUrl('');
                setEnabled(true);
                setAuthType('none');
                setAuthValue('');
                setCustomHeaderName('');
                setHasChanges(false);
            } else {
                setSaveError('Failed to delete webhook settings');
            }
        } catch (err) {
            setSaveError('An unexpected error occurred');
        } finally {
            setIsDeleting(false);
        }
    };

    const handleTest = async () => {
        if (!webhookSettings) {
            setTestResult({
                success: false,
                message: 'Please save webhook settings first',
            });
            return;
        }

        setIsTesting(true);
        setTestResult(null);

        try {
            const result = await testWebhook(activationId);
            setTestResult(result);
        } finally {
            setIsTesting(false);
        }
    };

    const handleReset = () => {
        setWebhookUrl(webhookSettings?.webhookUrl || '');
        setEnabled(webhookSettings?.enabled ?? true);
        setAuthType(webhookSettings?.authType || 'none');
        setCustomHeaderName(webhookSettings?.customHeaderName || '');
        setAuthValue('');
        setSaveError(null);
        setSaveSuccess(false);
        setTestResult(null);
    };

    const getAuthValueLabel = () => {
        switch (authType) {
            case 'bearer':
                return 'Bearer Token';
            case 'basic':
                return 'Credentials (username:password)';
            case 'custom_header':
                return 'Header Value';
            default:
                return 'Auth Value';
        }
    };

    const getAuthValuePlaceholder = () => {
        switch (authType) {
            case 'bearer':
                return 'Enter your bearer token';
            case 'basic':
                return 'username:password';
            case 'custom_header':
                return 'Enter the header value';
            default:
                return '';
        }
    };

    if (isLoading && !webhookSettings) {
        return <Loading isLoading={true} />;
    }

    if (permissionError) {
        return (
            <div className="space-y-6">
                <CardHeader>
                    <CardTitle>Access Denied</CardTitle>
                    <CardDescription>
                        You need administrator privileges to manage webhook
                        settings.
                    </CardDescription>
                </CardHeader>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <CardHeader>
                <CardTitle>Webhook Settings</CardTitle>
                <CardDescription>
                    Configure a webhook to be called whenever a share is created.
                    This allows integration with external services like Zapier,
                    Make, or your own systems.
                </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
                {/* Error Message */}
                {saveError && (
                    <div className="rounded-md bg-red-50 p-4">
                        <p className="text-sm font-medium text-red-800">
                            {saveError}
                        </p>
                    </div>
                )}

                {/* Success Message */}
                {saveSuccess && (
                    <div className="rounded-md bg-green-50 p-4">
                        <p className="text-sm font-medium text-green-800">
                            Webhook settings saved successfully!
                        </p>
                    </div>
                )}

                {/* Test Result */}
                {testResult && (
                    <div
                        className={`rounded-md p-4 ${
                            testResult.success ? 'bg-green-50' : 'bg-yellow-50'
                        }`}
                    >
                        <p
                            className={`text-sm font-medium ${
                                testResult.success
                                    ? 'text-green-800'
                                    : 'text-yellow-800'
                            }`}
                        >
                            {testResult.message}
                        </p>
                    </div>
                )}

                {/* Enable/Disable Toggle */}
                <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                        <Label htmlFor="webhook-enabled">Enable Webhook</Label>
                        <p className="text-sm text-muted-foreground">
                            When enabled, the webhook will be called for every
                            new share
                        </p>
                    </div>
                    <Switch
                        id="webhook-enabled"
                        checked={enabled}
                        onCheckedChange={setEnabled}
                        disabled={isSaving}
                    />
                </div>

                {/* Webhook URL */}
                <div>
                    <Label htmlFor="webhook-url">Webhook URL</Label>
                    <Input
                        id="webhook-url"
                        type="url"
                        value={webhookUrl}
                        onChange={(e) => setWebhookUrl(e.target.value)}
                        placeholder="https://hooks.zapier.com/..."
                        disabled={isSaving}
                        className="mt-2"
                    />
                    <p className="mt-1 text-sm text-muted-foreground">
                        The URL that will receive POST requests when shares are
                        created
                    </p>
                </div>

                {/* Auth Type */}
                <div>
                    <Label htmlFor="auth-type">Authentication</Label>
                    <Select
                        value={authType}
                        onValueChange={(value) => {
                            setAuthType(value as AuthType);
                            setAuthValue('');
                        }}
                        disabled={isSaving}
                    >
                        <SelectTrigger className="mt-2">
                            <SelectValue placeholder="Select authentication type" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="none">
                                No Authentication
                            </SelectItem>
                            <SelectItem value="bearer">Bearer Token</SelectItem>
                            <SelectItem value="basic">
                                Basic Authentication
                            </SelectItem>
                            <SelectItem value="custom_header">
                                Custom Header
                            </SelectItem>
                        </SelectContent>
                    </Select>
                    <p className="mt-1 text-sm text-muted-foreground">
                        Choose how to authenticate with the webhook endpoint
                    </p>
                </div>

                {/* Custom Header Name (only for custom_header type) */}
                {authType === 'custom_header' && (
                    <div>
                        <Label htmlFor="custom-header-name">
                            Custom Header Name
                        </Label>
                        <Input
                            id="custom-header-name"
                            type="text"
                            value={customHeaderName}
                            onChange={(e) => setCustomHeaderName(e.target.value)}
                            placeholder="X-API-Key"
                            disabled={isSaving}
                            className="mt-2"
                        />
                        <p className="mt-1 text-sm text-muted-foreground">
                            The name of the custom header to include
                        </p>
                    </div>
                )}

                {/* Auth Value (for all auth types except none) */}
                {authType !== 'none' && (
                    <div>
                        <Label htmlFor="auth-value">{getAuthValueLabel()}</Label>
                        <Input
                            id="auth-value"
                            type="password"
                            value={authValue}
                            onChange={(e) => setAuthValue(e.target.value)}
                            placeholder={getAuthValuePlaceholder()}
                            disabled={isSaving}
                            className="mt-2"
                        />
                        <p className="mt-1 text-sm text-muted-foreground">
                            {webhookSettings?.hasAuthConfigured && !authValue
                                ? 'Authentication is configured. Enter a new value to update it.'
                                : 'This value will be encrypted and stored securely'}
                        </p>
                    </div>
                )}

                {/* Status Info */}
                <div className="border-t pt-4">
                    {webhookSettings?.updatedAt ? (
                        <p className="text-sm text-muted-foreground">
                            Last updated:{' '}
                            {new Date(
                                webhookSettings.updatedAt,
                            ).toLocaleDateString()}
                        </p>
                    ) : (
                        <p className="text-sm text-muted-foreground">
                            No webhook configured yet
                        </p>
                    )}
                    {webhookSettings?.hasAuthConfigured && (
                        <p className="text-sm text-green-600 mt-1">
                            ✅ Authentication configured
                        </p>
                    )}
                </div>

                {/* Action Buttons */}
                <div className="flex flex-wrap gap-3 pt-4">
                    <Button
                        type="button"
                        onClick={handleSave}
                        disabled={!webhookUrl.trim() || isSaving}
                    >
                        {isSaving
                            ? 'Saving...'
                            : webhookSettings
                            ? 'Update Settings'
                            : 'Create Settings'}
                    </Button>

                    {hasChanges && (
                        <Button
                            type="button"
                            variant="outline"
                            onClick={handleReset}
                            disabled={isSaving}
                        >
                            Reset
                        </Button>
                    )}

                    {webhookSettings && (
                        <>
                            <Button
                                type="button"
                                variant="outline"
                                onClick={handleTest}
                                disabled={isTesting}
                            >
                                {isTesting ? 'Testing...' : 'Test Webhook'}
                            </Button>

                            <Button
                                type="button"
                                variant="destructive"
                                onClick={handleDelete}
                                disabled={isDeleting}
                            >
                                {isDeleting ? 'Deleting...' : 'Delete Webhook'}
                            </Button>
                        </>
                    )}
                </div>
            </CardContent>

            {/* Payload Documentation */}
            <CardHeader>
                <CardTitle className="text-lg">Webhook Payload</CardTitle>
                <CardDescription>
                    When a share is created, the following JSON payload will be
                    sent via POST request:
                </CardDescription>
            </CardHeader>
            <CardContent>
                <pre className="p-4 bg-muted rounded-lg text-sm overflow-x-auto">
                    {JSON.stringify(
                        {
                            destination: 'user@example.com',
                            shareIdentifiers: ['photo-123', 'photo-456'],
                            activationId: activationId,
                            galleryId: 12345,
                            shareType: 0,
                            createdAt: '2025-12-02T10:30:00.000Z',
                        },
                        null,
                        2,
                    )}
                </pre>
                <p className="mt-2 text-sm text-muted-foreground">
                    <strong>shareType:</strong> 0 = Email, 1 = SMS
                </p>
            </CardContent>
        </div>
    );
}

